import React, { useEffect, useMemo, useState } from "react";
import { useGetCentres } from "../../api/settings";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Icon } from "@iconify/react";

const DropdownIcon = (props) => (
  <Icon icon="eva:arrow-ios-downward-fill" width={18} height={18} {...props} />
);

function SpokeFilter({ hubIds = [], spokeIds = [], onChange, component }) {
  // Pagination states
  const [page, setPage] = useState(1);
  const limit = 50;
  const [allCentres, setAllCentres] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const { centres = [], isLoading, pagination } = useGetCentres({
    centerType: "hub",
    onlyActive: "active",
    page,
    limit,
  });

  const [search, setSearch] = useState("");

  // Append paginated centres
  useEffect(() => {
    if (!isLoading && Array.isArray(centres)) {
      setAllCentres((prev) => {
        if (page === 1) return [...centres];

        const existingIds = new Set(prev.map((item) => item.id));

        const newItems = centres.filter(
          (item) => !existingIds.has(item.id)
        );

        return [...prev, ...newItems];
      });

      setLoadingMore(false);
    }
  }, [centres, isLoading, page]);

  // Infinite scroll
  const handleScroll = (e) => {
    const target = e.currentTarget;

    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 50;

    const hasMore = page < (pagination?.totalPages || 1);

    if (isNearBottom && hasMore && !loadingMore) {
      setLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  /* ---------------- HUBS ---------------- */
  const allHubIds = useMemo(
    () => allCentres.map((h) => h.id),
    [allCentres]
  );

  useEffect(() => {
    if (allHubIds.length && hubIds.length === 0) {
      onChange({ hubIds: allHubIds, spokeIds: [] });
    }
  }, [allHubIds, hubIds, onChange]);

  /* ---------------- SPOKES ---------------- */
  const spokeOptions = useMemo(
    () =>
      allCentres
        .flatMap((h) => h.spokeCenters || [])
        .map((s) => ({
          label: s.name?.trim(),
          value: s.id,
        })),
    [allCentres]
  );

  /* ---------------- FILTERED SPOKES ---------------- */
  const filteredSpokes = spokeOptions.filter((s) =>
    s.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <FormControl size="small">
        <Select
          multiple
          value={spokeIds}
          displayEmpty
          IconComponent={DropdownIcon}
          onOpen={() => setSearch("")}
          onChange={(e) =>
            onChange({ hubIds: allHubIds, spokeIds: e.target.value })
          }
          renderValue={() => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <span>Spoke Centre</span>

              {spokeIds.length > 0 && (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    borderRadius: "50%",
                    backgroundColor: "#7C7CFF",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {spokeIds.length}
                </Box>
              )}
            </Box>
          )}
          sx={{
            height: 38,
            width: spokeIds.length > 0 ? 160 : 140,
            borderRadius: "10px",
            backgroundColor:
              ["interpretations", "referred-cases", "centres", "members"].includes(
                component
              )
                ? "var(--app-surface)"
                : "var(--app-surface)",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          MenuProps={{
            disableAutoFocusItem: true,
            autoFocus: false,
            PaperProps: {
              onScroll: handleScroll,
              sx: {
                mt: 1,
                width: 260,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: "12px",
                border: "1px solid var(--app-border)",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
              },
            },
          }}
        >
          {/* SEARCH */}
          <MenuItem disabled>
            <TextField
              fullWidth
              size="small"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "var(--app-text-muted)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </MenuItem>

          {/* Initial Loader */}
          {isLoading && page === 1 ? (
            <MenuItem disabled>
              <CircularProgress size={18} />
            </MenuItem>
          ) : filteredSpokes.length > 0 ? (
            filteredSpokes.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox
                  checked={spokeIds.includes(opt.value)}
                  size="small"
                />
                <ListItemText
                  primary={opt.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    noWrap: true,
                  }}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemText primary="No results found" />
            </MenuItem>
          )}

          {/* Bottom Lazy Loader */}
          {loadingMore && (
            <MenuItem disabled>
              <CircularProgress size={18} />
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
}

export default SpokeFilter;